import { useMemo, useState } from 'react';
import type {
  WorkflowRunHistoryDetail,
  WorkflowRunNodeStatus,
} from '@/apis/logiblocksAPI';
import { calculateGraphLayout } from '../graphLayout';
import {
  buildRunNodeStatusMap,
  getRunGraphSource,
  getRunGraphStatus,
} from '../runVisualization';
import { lbWorkflowToReactFlow } from '../serialization';
import type { ReactFlowEdge, ReactFlowNode } from '../types';
import { useDelayedFlag } from './useDelayedFlag';
import { useRunGraphSnapshot } from './useRunGraphSnapshot';
import { useWorkflowRunDetail } from './useWorkflowRunDetail';

// A run selection loads two chained fetches before its graph can be painted. A
// spinner is only worth showing past this wait, so a fast or cached load does
// not flash one.
const RUN_GRAPH_LOADING_DELAY_MS = 250;

/** A graph the canvas draws, as React Flow nodes and edges. */
export interface CanvasGraph {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
}

// Shown for a selected run whose graph cannot be shown, rather than painting its
// outcome onto the live graph. A stable reference so the display-graph memo and
// its consumers don't rerun on every render.
const EMPTY_GRAPH: CanvasGraph = {
  nodes: [],
  edges: [],
};

// Which notice floats over a selected run's graph, or null for none: 'older' is
// informational (the shown snapshot is not the current workflow); 'missing' and
// 'unavailable' both mean no graph can ever be shown and share one warning;
// 'load-failed' means the graph exists but could not be loaded this time.
export type RunGraphNotice =
  'older' | 'missing' | 'unavailable' | 'load-failed' | null;

/** The graph the canvas draws for the viewed run, and the notice that floats over it. */
export interface RunDisplay {
  /** The graph the run executed, or an empty graph while it loads or if it can't be shown. */
  graph: CanvasGraph;
  /** The notice to float over `graph`, or null for none. */
  notice: RunGraphNotice;
}

/** The run on screen, what it did, and how the canvas draws it. */
export interface RunView {
  /** The run being looked at. Null when no run is chosen. */
  viewedRunId: string | null;
  /** Makes `runId` the viewed run, or clears the viewed run if `runId` is null. */
  viewRun: (runId: string | null) => void;
  /**
   * What the viewed run did, reloaded while the run is going. Null outside the run view modes,
   * while the run first loads, and if its first load fails.
   */
  runDetail: WorkflowRunHistoryDetail | null;
  /** Why the viewed run could not be loaded, or null if nothing failed. */
  runDetailError: string | null;
  /** Goes up each time a response for the viewed run has a different status from the last one. */
  runStatusChangeCount: number;
  /** Reloads the viewed run, and shows a toast if the request fails. */
  refetchRunDetail: () => void;
  /** Each node's status in the viewed run, or null when no run detail has loaded. */
  runNodeStatuses: Map<string, WorkflowRunNodeStatus> | null;
  /** What the canvas draws for the viewed run, or null when the canvas draws the live graph. */
  runDisplay: RunDisplay | null;
  /** Whether the viewed run's detail or graph is still loading. */
  isRunGraphLoading: boolean;
  /** Whether the canvas shows its loading spinner for the viewed run's graph. */
  showRunGraphLoading: boolean;
}

export interface UseRunViewArgs {
  workflowId: string;
  /** Whether the workflow is a draft, which decides whether a run's graph is the current one. */
  isWorkflowDraft: boolean;
  /** The run to view first, from a link to that run. */
  initialRunId: string | undefined;
  /** Whether the canvas mode is one for viewing runs: the Runs tab or test results. */
  isRunViewMode: boolean;
}

/**
 * Returns the run on screen for the canvas and the panels beside it. The viewed run loads, and
 * reloads while it is going, only if `isRunViewMode` is true.
 */
export function useRunView({
  workflowId,
  isWorkflowDraft,
  initialRunId,
  isRunViewMode,
}: UseRunViewArgs): RunView {
  const [viewedRunId, setViewedRunId] = useState<string | null>(
    initialRunId ?? null
  );
  const {
    runDetail: loadedRunDetail,
    runDetailError,
    runStatusChangeCount,
    refetchRunDetail,
  } = useWorkflowRunDetail(workflowId, viewedRunId, isRunViewMode);
  // The canvas and panels get no run outside the run view modes, or they would draw it on the
  // live graph.
  const runDetail = isRunViewMode ? loadedRunDetail : null;
  const runNodeStatuses = useMemo(
    () => (runDetail === null ? null : buildRunNodeStatusMap(runDetail.nodes)),
    [runDetail]
  );

  // The graph that actually ran, fetched from the viewed run's published
  // version, or from a test run's stored graph, and cached across selections.
  // Null when no run is viewed or when the run predates version tracking and
  // so has no snapshot to show.
  const runGraphSource =
    loadedRunDetail === null ? null : getRunGraphSource(loadedRunDetail);
  const { snapshot: runGraphSnapshot, status: snapshotStatus } =
    useRunGraphSnapshot(workflowId, runGraphSource, isRunViewMode);

  // The viewed run's snapshot as React Flow nodes and edges, laid out the way the live
  // graph would be. Null while there is no snapshot, or when the snapshot can't be turned
  // into a graph.
  // React Flow hides a node until it measures the node's element, and it treats each new node
  // object as unmeasured. A React Flow bug: if new objects arrive just after a measure, it hides
  // the nodes again and never re-measures them, because the element's size hasn't changed.
  // Viewing a run changes `viewedRunId`, `runDetail` and `runGraphSnapshot` in separate
  // renders. Depending only on `runGraphSnapshot` keeps the same node objects through the
  // first two, so React Flow gets one new set per selection.
  const runGraph = useMemo((): CanvasGraph | null => {
    if (runGraphSnapshot === null) {
      return null;
    }
    try {
      const reactFlowGraph = lbWorkflowToReactFlow(runGraphSnapshot);
      return {
        nodes: calculateGraphLayout(reactFlowGraph.nodes, reactFlowGraph.edges),
        edges: reactFlowGraph.edges,
      };
    } catch (error) {
      // A snapshot that uses a node type since removed cannot be turned into a graph.
      console.error('Failed to render run graph snapshot:', error);
      return null;
    }
  }, [runGraphSnapshot]);

  // The graph to render for the viewed run and any notice to float over it. A
  // viewed run shows only the exact graph it executed (its snapshot). When that
  // snapshot cannot be shown, the run shows nothing and a notice, rather than
  // paint its outcome onto the live graph, which may have moved on: 'missing'
  // for a run predating version tracking, 'unavailable' when the bytes cannot
  // be turned into a graph (e.g. they use a node type since removed),
  // 'load-failed' when the fetch itself failed and retrying may succeed,
  // 'older' when the snapshot is shown but is not the current workflow. A test
  // run's stored graph is shown with no notice. Null when no run is shown, so
  // the canvas draws the live graph being edited.
  const runDisplay = useMemo((): RunDisplay | null => {
    if (!isRunViewMode || viewedRunId === null || runDetail === null) {
      return null;
    }
    const graphStatus = getRunGraphStatus(runDetail, isWorkflowDraft);
    if (graphStatus === 'missing') {
      return { graph: EMPTY_GRAPH, notice: 'missing' };
    }
    if (snapshotStatus === 'unavailable') {
      return { graph: EMPTY_GRAPH, notice: 'unavailable' };
    }
    if (snapshotStatus === 'load-failed') {
      return { graph: EMPTY_GRAPH, notice: 'load-failed' };
    }
    if (runGraphSnapshot === null) {
      // Snapshot still loading: show nothing yet, no notice.
      return { graph: EMPTY_GRAPH, notice: null };
    }
    if (runGraph === null) {
      return { graph: EMPTY_GRAPH, notice: 'unavailable' };
    }
    return {
      graph: runGraph,
      notice: graphStatus === 'older' ? 'older' : null,
    };
  }, [
    isRunViewMode,
    viewedRunId,
    runDetail,
    runGraphSnapshot,
    runGraph,
    snapshotStatus,
    isWorkflowDraft,
  ]);

  // Whether the viewed run's graph is still loading: its detail is not yet the
  // viewed run's, or its snapshot has neither loaded nor settled on a notice.
  // This mirrors the display memo's silent empty-canvas branch, so the spinner
  // shows exactly when the canvas would otherwise sit blank. An error resolves
  // it, since a notice takes over from there.
  const isRunGraphLoading =
    isRunViewMode &&
    viewedRunId !== null &&
    runDetailError === null &&
    !(
      runDetail?.id === viewedRunId &&
      ((runDisplay !== null && runDisplay.notice !== null) ||
        runGraphSnapshot !== null)
    );
  const showRunGraphLoading = useDelayedFlag(
    isRunGraphLoading,
    RUN_GRAPH_LOADING_DELAY_MS,
    viewedRunId
  );

  return {
    viewedRunId,
    viewRun: setViewedRunId,
    runDetail,
    runDetailError,
    runStatusChangeCount,
    refetchRunDetail,
    runNodeStatuses,
    runDisplay,
    isRunGraphLoading,
    showRunGraphLoading,
  };
}
